import * as React from "react";
import { Formik } from "formik";
import * as Yup from "yup";

type Props = { jobId: number; captchaSiteKey?: string };
type Fields = Record<string, string>;
type Values = {
  name: string;
  email: string;
  mobile: string;
  linkedinUrl: string;
  coverLetter: string;
  resume: File | null;
};

const allowedTypes = new Set(["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"]);
const maxBytes = 5 * 1024 * 1024;
const validationSchema = Yup.object({
  name: Yup.string().trim().required("Name is required."),
  email: Yup.string().trim().email("Enter a valid email address.").required("Email is required."),
  mobile: Yup.string().matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit Indian mobile number.").required("Mobile number is required."),
  linkedinUrl: Yup.string().trim().url("Enter a valid LinkedIn URL.").nullable(),
  coverLetter: Yup.string(),
  resume: Yup.mixed<File>().required("A PDF, DOC, or DOCX resume is required.").test("file-type-and-size", "Choose a PDF, DOC, or DOCX file no larger than 5 MB.", (file) => Boolean(file && allowedTypes.has(file.type) && file.size <= maxBytes)),
});

function messageFor(status: number, body: { message?: string; fields?: Fields }) {
  if (status === 404) return "This job is no longer accepting applications.";
  if (status === 409) return "You have already applied for this job.";
  if (status === 429) return "Please wait a moment before trying again.";
  return body.message || "Unable to submit your application right now.";
}

export default function ApplicationForm({ jobId, captchaSiteKey }: Props) {
  const [message, setMessage] = React.useState("");
  const [progress, setProgress] = React.useState<number | null>(null);
  const [success, setSuccess] = React.useState(false);

  const submit = async (values: Values, helpers: any) => {
    setMessage("");
    const data = new FormData();
    data.set("name", values.name.trim());
    data.set("email", values.email.trim());
    data.set("mobile", `+91${values.mobile}`);
    if (values.resume) data.set("resume", values.resume, values.resume.name);
    if (values.linkedinUrl.trim()) data.set("linkedinUrl", values.linkedinUrl.trim());
    if (values.coverLetter.trim()) data.set("coverLetter", values.coverLetter.trim());
    if (captchaSiteKey && typeof window !== "undefined" && (window as any).grecaptcha?.execute) {
      data.set("captchaToken", await (window as any).grecaptcha.execute(captchaSiteKey, { action: "apply" }));
    }

    setProgress(0);
    await new Promise<void>((resolve) => {
      const request = new XMLHttpRequest();
      request.open("POST", `/api/jobs/${jobId}/applications`);
      request.upload.onprogress = (upload) => {
        if (upload.lengthComputable) setProgress(Math.round((upload.loaded / upload.total) * 100));
      };
      request.onload = () => {
        let body: { message?: string; fields?: Fields } = {};
        try { body = JSON.parse(request.responseText); } catch { /* fall back to a generic message */ }
        setProgress(null);
        if (request.status >= 200 && request.status < 300) {
          setSuccess(true);
          setMessage(body.message || "Your application submitted successfully!");
        } else {
          helpers.setErrors((body.fields || {}) as Partial<Record<keyof Values, string>>);
          setMessage(messageFor(request.status, body));
        }
        resolve();
      };
      request.onerror = () => {
        setProgress(null);
        setMessage("Unable to submit your application right now.");
        resolve();
      };
      request.send(data);
    });
  };

  if (success) return <p role="status">{message}</p>;

  return (
    <Formik<Values>
      initialValues={{ name: "", email: "", mobile: "", linkedinUrl: "", coverLetter: "", resume: null }}
      validationSchema={validationSchema}
      onSubmit={submit}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldTouched, setFieldValue, touched, values }) => (
        <form className="application-form" onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
          <h2 id="application-title">Apply for this job</h2>
          <label>Name<input name="name" value={values.name} onBlur={handleBlur} onChange={handleChange} autoComplete="name" /></label>{touched.name && errors.name ? <p role="alert">{errors.name}</p> : null}
          <label>Email<input name="email" type="email" value={values.email} onBlur={handleBlur} onChange={handleChange} autoComplete="email" /></label>{touched.email && errors.email ? <p role="alert">{errors.email}</p> : null}
          <label>Mobile number
            <span className="application-form__mobile-field"><span aria-hidden="true">+91</span><input name="mobile" value={values.mobile} onBlur={handleBlur} onChange={(event) => setFieldValue("mobile", event.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" autoComplete="tel-national" placeholder="9876543210" aria-describedby="mobile-help" /></span>
          </label>
          <p id="mobile-help" className="application-form__hint">Enter your 10-digit mobile number.</p>
          {touched.mobile && errors.mobile ? <p role="alert">{errors.mobile}</p> : null}
          <label>LinkedIn URL (optional)<input name="linkedinUrl" type="url" value={values.linkedinUrl} onBlur={handleBlur} onChange={handleChange} autoComplete="url" /></label>{touched.linkedinUrl && errors.linkedinUrl ? <p role="alert">{errors.linkedinUrl}</p> : null}
          <label>Cover letter (optional)<textarea name="coverLetter" value={values.coverLetter} onBlur={handleBlur} onChange={handleChange} /></label>
          <label>Resume (PDF, DOC, or DOCX; 5 MB max)<input name="resume" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onBlur={() => setFieldTouched("resume")} onChange={(event) => { void setFieldValue("resume", event.currentTarget.files?.[0] || null); void setFieldTouched("resume", true); }} /></label>
          {touched.resume && errors.resume ? <p role="alert">{errors.resume as string}</p> : null}
          {progress !== null ? <p role="status">Uploading {progress}%…</p> : null}
          {message ? <p role="alert">{message}</p> : null}
          <button type="submit" disabled={isSubmitting || progress !== null}>{isSubmitting || progress !== null ? "Submitting…" : "Submit application"}</button>
        </form>
      )}
    </Formik>
  );
}
